import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, Mail, Clock, CreditCard, Wrench, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isPrimaryAdmin } from '../utils/roleRouting';

const StaffNotifications = ({ api, isAdmin }) => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState(() => localStorage.getItem('notif_last_seen') || '');
  const ref = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCarlos = isPrimaryAdmin(user);
  // « À faire » (Carlos) — total des actions en attente (GET /admin/carlos/todo/count).
  const [todoCount, setTodoCount] = useState(0);
  const [todoUnavailable, setTodoUnavailable] = useState(false);

  const loadTodoCount = useCallback(async () => {
    if (!isCarlos || todoUnavailable) return;
    try {
      const res = await api.get('/admin/carlos/todo/count');
      const total = Number(res.data?.total);
      setTodoCount(Number.isFinite(total) && total > 0 ? total : 0);
    } catch (err) {
      if (err?.response?.status === 404) {
        // Backend plus ancien sans endpoint « À faire » : on n'insiste pas.
        setTodoUnavailable(true);
      }
      setTodoCount(0);
    }
  }, [api, isCarlos, todoUnavailable]);

  useEffect(() => {
    if (!isCarlos) return undefined;
    loadTodoCount();
    const interval = setInterval(loadTodoCount, 30000);
    return () => clearInterval(interval);
  }, [isCarlos, loadTodoCount]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const allNotifs = [];

      // Fetch pending invoice requests
      try {
        const facture = await api.get('/facture-request/pending');
        const pending = (facture.data.requests || []).map(r => ({
          id: r.id,
          type: 'facture_email',
          title: 'Changement email facture',
          detail: `${r.nom} - ${r.ref_vente}`,
          subtitle: `${r.original_email} → ${r.new_email}`,
          time: r.created_at,
          link: '/admin/demandes-factures',
        }));
        allNotifs.push(...pending);
      } catch {}

      // Fetch pending devis
      try {
        const devis = await api.get('/admin/devis', { params: { status: 'en_attente' } });
        const devisData = Array.isArray(devis.data) ? devis.data : (devis.data?.devis || devis.data || []);
        const devisNotifs = (Array.isArray(devisData) ? devisData : []).slice(0, 5).map(d => ({
          id: d.id,
          type: 'devis',
          title: 'Devis en attente',
          detail: `${d.client_nom || 'Client'} - ${d.total?.toFixed(2) || '0'} EUR`,
          subtitle: d.ref_devis || '',
          time: d.created_at,
          link: '/devis-factures',
        }));
        allNotifs.push(...devisNotifs);
      } catch {}

      // Fetch recent acomptes (last 24h)
      try {
        const now = new Date();
        const yesterday = new Date(now - 86400000).toISOString();
        const acompteRes = await api.get(`/comptabilite/factures-acomptes?date_debut=${yesterday.slice(0,10)}&date_fin=${now.toISOString().slice(0,10)}`);
        const recentAcomptes = (acompteRes.data?.acomptes_recents || []).slice(0, 5);
        const acompteNotifs = recentAcomptes.map(a => ({
          id: a.id,
          type: 'acompte',
          title: 'Acompte recu',
          detail: `${a.device_ref || a.commande_id || '-'} — ${(a.montant || 0).toFixed(2)} EUR`,
          subtitle: a.mode_paiement || '',
          time: a.date_versement,
          link: '/comptabilite',
        }));
        allNotifs.push(...acompteNotifs);
      } catch {}

      // Fetch devices with recent status changes (last 24h)
      try {
        const devices = await api.get('/devices?limit=20');
        const devicesData = devices.data || [];
        const now = Date.now();
        const statusNotifs = [];
        for (const d of devicesData) {
          const timeline = d.timeline || [];
          if (timeline.length > 0) {
            const latest = timeline[timeline.length - 1];
            const eventTime = new Date(latest.timestamp || '').getTime();
            if (now - eventTime < 86400000) {
              statusNotifs.push({
                id: `${d.ref}-${timeline.length}`,
                type: 'repair_status',
                title: 'Statut reparation',
                detail: `${d.ref} — ${latest.label || latest.status || 'Mise a jour'}`,
                subtitle: d.client_name || '',
                time: latest.timestamp,
                link: `/reparations/${d.ref}`,
              });
            }
          }
        }
        allNotifs.push(...statusNotifs.slice(0, 5));
      } catch {}

      // Sort by most recent
      allNotifs.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
      setNotifications(allNotifs.slice(0, 20));
    } catch (err) {
      console.error('Notifications error:', err);
    }
  }, [api, isAdmin]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleClick = (notif) => {
    navigate(notif.link);
    setOpen(false);
  };

  // Mark all as seen when dropdown opens
  const handleBellClick = () => {
    const wasOpen = open;
    setOpen(!wasOpen);
    if (!wasOpen && notifications.length > 0) {
      const now = new Date().toISOString();
      localStorage.setItem('notif_last_seen', now);
      setLastSeenAt(now);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return "A l'instant";
    if (diff < 60) return `${diff}min`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const iconMap = {
    facture_email: <Mail className="w-3.5 h-3.5 text-yellow-400" />,
    devis: <FileText className="w-3.5 h-3.5 text-cyan-400" />,
    acompte: <CreditCard className="w-3.5 h-3.5 text-amber-400" />,
    repair_status: <Wrench className="w-3.5 h-3.5 text-emerald-400" />,
  };

  if (!isAdmin) return null;

  const unseenNotifs = lastSeenAt
    ? notifications.filter(n => n.time && new Date(n.time) > new Date(lastSeenAt)).length
    : notifications.length;
  // Les « À faire » restent comptés tant qu'ils ne sont pas traités (pas de « vu »).
  const unseenCount = unseenNotifs + (isCarlos ? todoCount : 0);
  const showTodoEntry = isCarlos && !todoUnavailable;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleBellClick}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="staff-notifications-btn"
      >
        <Bell className="w-5 h-5 text-gray-300" />
        {unseenCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="notification-badge">
            {unseenCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-auto bg-gray-900 border border-white/15 rounded-xl shadow-2xl z-50" data-testid="notifications-dropdown">
            <div className="sticky top-0 px-4 py-3 border-b border-white/10 bg-gray-900/95 backdrop-blur-sm">
              <h3 className="text-sm font-bold text-white">Notifications</h3>
              <p className="text-xs text-gray-500">{unseenCount > 0 ? `${unseenCount} nouvelle${unseenCount !== 1 ? 's' : ''}` : 'Tout est lu'}</p>
            </div>

            {showTodoEntry && (
              <button
                onClick={() => { navigate('/admin/a-faire'); setOpen(false); }}
                className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/10"
                data-testid="notif-todo-entry"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-white/5">
                    <ClipboardList className={`w-3.5 h-3.5 ${todoCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0 flex items-center justify-between">
                    <span className="text-xs font-semibold text-white">À faire ({todoCount})</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${todoCount > 0 ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                      {todoCount > 0 ? 'en attente' : 'à jour'}
                    </span>
                  </div>
                </div>
              </button>
            )}

            {notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">
                Aucune notification
              </div>
            ) : (
              <div>
                {notifications.map((n) => (
                  <button
                    key={`${n.type}-${n.id}`}
                    onClick={() => handleClick(n)}
                    className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                    data-testid={`notif-${n.type}-${n.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 p-1.5 rounded-lg bg-white/5">
                        {iconMap[n.type] || <Clock className="w-3.5 h-3.5 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-white">{n.title}</span>
                          <span className="text-[10px] text-gray-500 ml-2 shrink-0">{formatTime(n.time)}</span>
                        </div>
                        <p className="text-xs text-gray-300 mt-0.5 truncate">{n.detail}</p>
                        {n.subtitle && <p className="text-[10px] text-gray-500 mt-0.5 truncate">{n.subtitle}</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StaffNotifications;
